import "./MenuDrawer.scss";

import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Popover,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import {
  Clear as ClearIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Login as LoginIcon,
  MoreHoriz as MoreIcon,
  ManageSearch as SearchIcon,
  Settings as SettingsIcon,
  PersonAdd as SignUpIcon
} from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import {
  darkModeToggled,
  sessionDeleted,
  sessionRenamed
} from "../segment/Events";
import {
  handleDeleteChat,
  handleSignIn,
  handleSignOut,
  renameSession,
  setActiveSession,
  setBot,
  setDrawerOpen,
  setIsLoading,
  setUserDarkMode
} from "../redux/chatSlice";
import { useDispatch, useSelector } from "react-redux";

import { bots } from "../utils/Bots";
import { getTimeSortedChatSessions } from "../utils/Functions";

function MenuDrawer() {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.chat.user);
  const drawerOpen = useSelector((state) => state.chat.drawerOpen);
  const chatSessions = useSelector((state) => state.chat.chatSessions);
  const activeSession = useSelector((state) => state.chat.activeSession);
  const darkMode = useSelector((state) => state.chat.darkMode);

  const sortedSessions = getTimeSortedChatSessions(chatSessions);

  const [chatMenuEl, setChatMenuEl] = useState(null);
  const [settingsEl, setSettingsEl] = useState(null);
  const [sessionMenu, setSessionMenu] = useState(null);
  const [renamingSession, setRenamingSession] = useState(null);
  const [newName, setNewName] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState(null);

  const renameInputRef = useRef(null);
  const searchInputRef = useRef(null);

  const handleMenuOpen = (event, sessionId) => {
    setChatMenuEl(event.currentTarget);
    setSessionMenu(sessionId);
    setSettingsEl(null);
    setSettingsOpen(false);
  };

  const handleMenuClose = () => {
    setChatMenuEl(null);
    setSessionMenu(null);
    setSearchOpen(false);
    setSearchText(null);
    handleSettingsClose();
  };

  const handleSettingsOpen = (event) => {
    setSettingsEl(event.target);
    setSettingsOpen(true);
    setChatMenuEl(null);
    setSessionMenu(null);
  };

  const handleSettingsClose = () => {
    setSettingsEl(null);
    setSettingsOpen(false);
  };

  const handleRenameSubmit = (sessionId) => {
    const session = chatSessions.find((session) => session.id === sessionId);

    if (!session) {
      console.error("Could not find session to rename");
    } else if (session.snippet !== newName) {
      dispatch(renameSession({ id: sessionId, newName }));
    }

    sessionRenamed(session);
    setRenamingSession(null);
    setNewName("");
  };

  const handleSearchToggle = () => {
    if (!searchOpen) {
      setSearchOpen(true);
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      handleMenuClose();
    }
  };

  useEffect(() => {
    if (renamingSession && renameInputRef.current) {
      renameInputRef.current.focus();
    }
  }, [renamingSession]);

  // close popup menu on scroll
  useEffect(() => {
    const drawerSessionsElement = document.querySelector(".drawer-sessions");

    const handleScroll = () => {
      if (sessionMenu) {
        handleMenuClose();
      }
    };

    drawerSessionsElement?.addEventListener("scroll", handleScroll);

    return () => {
      drawerSessionsElement?.removeEventListener("scroll", handleScroll);
    };
  });

  const filteredSessions = Object.keys(sortedSessions)
    .map((group) => {
      const filteredGroup = sortedSessions[group].filter((session) =>
        searchText
          ? `${session.snippet} ${session.bot} ${session.messages
              .map((message) => message.message)
              .join(" ")}`
              .trim()
              .toLowerCase()
              .includes(searchText.toLowerCase())
          : true
      );
      return { group, sessions: filteredGroup };
    })
    .filter((group) => group.sessions.length > 0);

  const hasResults = filteredSessions.length > 0;

  return (
    <Drawer
      onClose={() => {
        handleMenuClose();
        dispatch(setDrawerOpen(!drawerOpen));
      }}
      anchor="left"
      open={drawerOpen}
      className="drawer"
    >
      <div className="drawer-content">
        <Box className="drawer-stickyHeader">
          <div className="drawer-headerTop">
            <div className="drawer-headerLeft">
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Chat Logs
              </Typography>
              <IconButton onClick={() => handleSearchToggle()} size="large">
                <SearchIcon className="drawer-searchIcon" />
              </IconButton>
            </div>
            <IconButton
              onClick={() => {
                handleMenuClose();
                dispatch(setDrawerOpen(false));
              }}
              size="large"
            >
              <CloseIcon className="drawer-closeIcon" />
            </IconButton>
          </div>
          {searchOpen && (
            <TextField
              className="drawer-search"
              inputRef={searchInputRef}
              value={searchText}
              onClick={() => {
                setChatMenuEl(null);
                setSessionMenu(null);
                handleSettingsClose();
              }}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              placeholder="Search chat sessions"
              InputProps={{
                endAdornment: searchText ? (
                  <InputAdornment className="drawer-searchClear" position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchText("")}
                      aria-label="clear search text"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          )}
        </Box>
        <List
          className="drawer-sessions"
          sx={{ flexGrow: 1, overflow: "auto" }}
        >
          {hasResults ? (
            filteredSessions.map(({ group, sessions }) => (
              <>
                <ListItem key={group} className="drawer-groupTitle">
                  {group}
                </ListItem>
                {sessions
                  .sort((a, b) => b.lastActive - a.lastActive)
                  .map((session) => (
                    <ListItem
                      key={`session-${session.id}`}
                      className={`drawer-session ${
                        renamingSession === session.id
                          ? "drawer-sessionEditing"
                          : ""
                      } ${
                        session.id === activeSession
                          ? "drawer-sessionActive"
                          : ""
                      }`}
                    >
                      {renamingSession === session.id ? (
                        <TextField
                          className="drawer-sessionRename"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          onBlur={() => handleRenameSubmit(session.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              handleRenameSubmit(session.id);
                          }}
                          inputRef={renameInputRef}
                          size="small"
                          placeholder="Enter new chat name"
                        />
                      ) : (
                        <ListItemText
                          className="drawer-sessionSnippet"
                          primary={
                            <span className="drawer-snippetFade">
                              {session.snippet}
                            </span>
                          }
                          onClick={() => {
                            dispatch(setBot(bots[session.bot]));
                            dispatch(setActiveSession(session.id));
                            dispatch(setIsLoading(false));
                            handleMenuClose();
                            dispatch(setDrawerOpen(false));
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          if (sessionMenu && sessionMenu === session.id) {
                            handleMenuClose();
                          } else {
                            handleMenuOpen(event, session.id);
                          }
                        }}
                        className="drawer-session-menuButton"
                      >
                        <MoreIcon className="drawer-session-menuIcon" />
                      </IconButton>
                      {sessionMenu && sessionMenu === session.id && (
                        <Popover
                          className="drawer-sessionMenuContainer"
                          open={sessionMenu === session.id}
                          anchorEl={chatMenuEl}
                          onClose={() => setSessionMenu(null)}
                          hideBackdrop
                          anchorOrigin={{
                            vertical: "center",
                            horizontal: "right"
                          }}
                          transformOrigin={{
                            vertical: "center",
                            horizontal: "left"
                          }}
                        >
                          <div
                            className="drawer-sessionMenuOption drawer-sessionMenuRename"
                            onClick={() => {
                              setRenamingSession(session.id);
                              setNewName(session.snippet);
                              handleMenuClose();
                            }}
                          >
                            <IconButton
                              className="drawer-sessionMenuIcon"
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                            Rename
                          </div>
                          <div
                            className="drawer-sessionMenuOption drawer-sessionMenuDelete"
                            onClick={() => {
                              sessionDeleted(session);
                              dispatch(handleDeleteChat(session.id));
                            }}
                          >
                            <IconButton
                              className="drawer-sessionMenuIcon"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                            Delete
                          </div>
                        </Popover>
                      )}
                    </ListItem>
                  ))}
              </>
            ))
          ) : (
            <ListItem className="drawer-noResults">No Results</ListItem>
          )}
        </List>
        <Box
          className="drawer-bottom"
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end"
          }}
        >
          {user ? (
            <Box
              className="drawer-bottomContainer"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Box
                className="drawer-userContainer"
                sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}
              >
                <Avatar sx={{ mr: 1, bgcolor: "#047dff" }}>
                  {user.displayName ? user.displayName[0].toUpperCase() : null}
                </Avatar>
                <Box>
                  <Typography variant="body2">{user.displayName}</Typography>
                  <Typography variant="body2">{user.email}</Typography>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => dispatch(handleSignOut())}
                    sx={{ mt: 1 }}
                  >
                    Sign Out
                  </Button>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={(event) => {
                  if (settingsOpen) {
                    handleSettingsClose();
                  } else {
                    handleSettingsOpen(event);
                  }
                }}
                className="drawer-settings-button"
              >
                <SettingsIcon className="drawer-settings-icon" />
              </IconButton>
              <Popover
                className="drawer-settings"
                open={settingsOpen}
                anchorEl={settingsEl}
                hideBackdrop
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right"
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left"
                }}
              >
                <List className="drawer-links">
                  <ListItem className="drawer-linkItem">
                    <a
                      className="drawer-link"
                      href="https://www.askmn.ai/"
                      rel="noreferrer"
                      target="_blank"
                    >
                      💻 Visit Our Site
                    </a>
                  </ListItem>
                  <ListItem className="drawer-linkItem">
                    <a
                      className="drawer-link"
                      href="https://www.askmn.ai/privacy-policy"
                      rel="noreferrer"
                      target="_blank"
                    >
                      🔒 Privacy Policy
                    </a>
                  </ListItem>
                  <ListItem className="drawer-linkItem">
                    <a
                      className="drawer-link"
                      href="https://www.askmn.ai/terms-and-conditions"
                      rel="noreferrer"
                      target="_blank"
                    >
                      📜 Terms of Use
                    </a>
                  </ListItem>
                </List>
                <div
                  className="drawer-settingsItem drawer-settingsColorMode"
                  onClick={() => {
                    darkModeToggled(!darkMode);
                    dispatch(setUserDarkMode(!darkMode));
                  }}
                >
                  <Switch
                    checked={darkMode}
                    onChange={() => {
                      darkModeToggled(!darkMode);
                      dispatch(setUserDarkMode(!darkMode));
                    }}
                    color="primary"
                  />
                  {darkMode ? "☀️ Light Mode" : "🌙 Dark Mode"}
                </div>
              </Popover>
            </Box>
          ) : (
            <Box>
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                startIcon={<LoginIcon />}
                onClick={() => dispatch(handleSignIn())}
                sx={{ mb: 1 }}
              >
                Sign In
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                startIcon={<SignUpIcon />}
                onClick={() => dispatch(handleSignIn())} // Using handleSignIn for Sign Up as well
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Box>
      </div>
    </Drawer>
  );
}

export default MenuDrawer;
